import { useState } from 'react';
import { useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { breakpoints } from 'styles';
import { ModalButtonWithForm, User } from 'components/common';
import { CheckboxInput } from 'components/common/form/common';

import type { GetAchievementGroupCompletionsRes } from 'shared-types/responses';

interface Props {
  groupCompletion: GetAchievementGroupCompletionsRes['groupCompletions'][number];
  updateGroups: () => Promise<void>;
}

const AchievementGroupOfUsersRemoveMembers = ({
  groupCompletion,
  updateGroups
}: Props) => {
  const { dispatch } = useDispatch();

  const {
    messages,
    pageState: { achievement },
    theme
  } = useReduxState();

  const [members, setMembers] = useState<Record<string, boolean>>(
    groupCompletion.members.reduce((acc, member) => {
      acc[member.id] = false;
      return acc;
    }, {} as Record<number, boolean>)
  );

  const handleReset = () =>
    setMembers(
      groupCompletion.members.reduce((acc, member) => {
        acc[member.id] = false;
        return acc;
      }, {} as Record<number, boolean>)
    );

  const handleCardClick = (memberId: number, ev: React.MouseEvent) => {
    ev.stopPropagation();
    if (onlyOneUnchecked && !members[memberId.toString()]) return;

    setMembers((prev) => ({
      ...prev,
      [memberId.toString()]: !prev[memberId.toString()]
    }));
  };

  const submit = async () => {
    try {
      const removedMembers = Object.keys(members)
        .filter((key) => members[key])
        .map(Number);

      await api.post(
        `/admin/achievements/${achievement.id}` +
          `/group-completions/${groupCompletion.achievementData.id}/members`,
        { removedMembers }
      );

      await updateGroups();

      dispatch.addNotice({
        text: messages.achievementGroupConversationRemoveParticipantsSuccess,
        type: 'success'
      });
    } catch (err) {
      await dispatch.handleErrorDispatch(err);
    }
  };

  const onlyOneUnchecked =
    Object.values(members).filter((value) => value === false).length === 1;

  const submitDisabled = Object.values(members).every(
    (value) => value === false
  );

  return (
    <ModalButtonWithForm
      buttonCategory="danger"
      buttonIcon="user-minus"
      buttonIsDropdown={true}
      buttonSize="header"
      buttonText={messages.achievementGroupConversationRemoveParticipants}
      buttonTextHideBreakpoint={breakpoints.medium - 1}
      contentStyle={{ padding: 0 }}
      modalActionText={messages.commonRemoveUsersSelected}
      modalTitle={messages.achievementGroupConversationRemoveParticipants}
      modalWidth="small"
      onHide={handleReset}
      onShow={handleReset}
      onSubmit={submit}
      submitDisabled={submitDisabled}
    >
      {() => {
        return (
          <div className="users">
            {groupCompletion.members.map((member) => (
              <div
                className="member"
                key={member.id}
                onClick={(ev) => handleCardClick(member.id, ev)}
                {...(onlyOneUnchecked && !members[member.id.toString()]
                  ? {
                      'data-tooltip':
                        messages.achievementGroupConversationRemoveParticipantsWarning
                    }
                  : {})}
              >
                <CheckboxInput
                  checked={members[member.id.toString()]}
                  disabled={onlyOneUnchecked && !members[member.id.toString()]}
                  id={`removed-members-${member.id}`}
                  onClick={(ev) => ev.stopPropagation()}
                />

                <User user={member} />
              </div>
            ))}

            <style jsx>{`
              .users {
                display: flex;
                flex-direction: column;
                gap: 10px;
                padding: 10px;
              }
              .member {
                border-radius: ${theme.cardBorderRadius};
                border: 1px solid ${theme.lightGreyDark};
                cursor: pointer;
                display: flex;
                align-items: center;
                gap: 15px;
                padding: 10px 15px;
              }
            `}</style>
          </div>
        );
      }}
    </ModalButtonWithForm>
  );
};

export default AchievementGroupOfUsersRemoveMembers;
